(() => {
  const theme = window.FootleTheme || {};
  const root = theme.routes?.root || '/';
  const partnerBenefit = theme.partnerBenefit || {};
  const giftVariantId = Number(partnerBenefit.giftVariantId || 0);
  const giftProductHandle = String(partnerBenefit.giftProductHandle || '');
  const eligibleProductHandles = new Set(partnerBenefit.eligibleProductHandles || []);
  const partnerStorageKey = 'footle_partner_benefit_v1';
  const lastTrigger = new WeakMap();

  const select = (selector, scope = document) => scope.querySelector(selector);
  const selectAll = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  function ajaxUrl(path) {
    const rootPath = root.endsWith('/') ? root : `${root}/`;
    return `${rootPath}${String(path || '').replace(/^\/+/, '')}`.replace(/\/+/g, '/');
  }

  function normalizedPartnerKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  function partnerByKey(value) {
    return partnerBenefit.partners?.[normalizedPartnerKey(value)] || null;
  }

  function partnerByCode(value) {
    const code = String(value || '').trim().toUpperCase();
    return Object.values(partnerBenefit.partners || {}).find((partner) => partner.code === code) || null;
  }

  function storePartner(key, partner) {
    try {
      const days = Number(partnerBenefit.attributionDays || 30);
      window.localStorage.setItem(partnerStorageKey, JSON.stringify({
        key,
        code: partner.code,
        expiresAt: Date.now() + days * 24 * 60 * 60 * 1000,
      }));
    } catch {
      // Cart attributes still preserve attribution if local storage is unavailable.
    }
  }

  function storedPartner() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(partnerStorageKey) || 'null');
      if (!stored || Number(stored.expiresAt || 0) < Date.now()) {
        window.localStorage.removeItem(partnerStorageKey);
        return null;
      }
      return partnerByKey(stored.key) || partnerByCode(stored.code);
    } catch {
      return null;
    }
  }

  function activePartner(cart) {
    const queryKey = normalizedPartnerKey(new URL(window.location.href).searchParams.get('partner'));
    const queryPartner = partnerByKey(queryKey);
    if (queryPartner) {
      storePartner(queryKey, queryPartner);
      return queryPartner;
    }
    return storedPartner() || partnerByCode(cart?.attributes?.['Partner source']);
  }

  function partnerText(template, partner) {
    return String(template || '').replace('__PARTNER__', partner?.label || '');
  }

  function isPartnerGift(item) {
    return giftVariantId > 0 && Number(item?.variant_id || item?.id || 0) === giftVariantId;
  }

  function customerItemCount(cart) {
    return (cart?.items || []).reduce((count, item) => count + (isPartnerGift(item) ? 0 : item.quantity), 0);
  }

  function eligibleShoeQuantity(cart) {
    return (cart?.items || []).reduce((count, item) => {
      return count + (eligibleProductHandles.has(item.handle) ? item.quantity : 0);
    }, 0);
  }

  function renderPartnerBenefitNotice(partner) {
    const existing = select('[data-partner-benefit-notice]');
    if (!partner) {
      existing?.remove();
      return;
    }
    const notice = existing || document.createElement('aside');
    notice.className = 'partner-benefit-notice full-width';
    notice.dataset.partnerBenefitNotice = '';
    notice.innerHTML = `
      <div class="page-gutter">
        <strong>${theme.strings?.partnerBenefitLabel || ''}</strong>
        <span>${partnerText(theme.strings?.partnerBenefitNotice, partner)}</span>
      </div>
    `;
    if (!existing) select('main')?.before(notice);
  }

  function focusable(dialog) {
    return selectAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      dialog,
    ).filter((element) => !element.hidden && element.offsetParent !== null);
  }

  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    lastTrigger.set(dialog, trigger || document.activeElement);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('is-scroll-locked');
    requestAnimationFrame(() => {
      const preferred = select('.drawer-head [data-dialog-close]', dialog) || focusable(dialog)[0];
      preferred?.focus();
    });
  }

  function closeDialog(dialog) {
    if (!dialog?.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  function finishDialogClose(dialog) {
    document.documentElement.classList.remove('is-scroll-locked');
    lastTrigger.get(dialog)?.focus?.();
  }

  selectAll('dialog[data-mobile-menu], dialog[data-cart-dialog]').forEach((dialog) => {
    dialog.addEventListener('close', () => finishDialogClose(dialog));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const items = focusable(dialog);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    selectAll('[data-dialog-close]', dialog).forEach((button) => {
      button.addEventListener('click', () => closeDialog(dialog));
    });
  });

  selectAll('[data-menu-open]').forEach((trigger) => {
    trigger.addEventListener('click', () => openDialog(select('[data-mobile-menu]'), trigger));
  });

  selectAll('[data-localization-select]').forEach((picker) => {
    picker.addEventListener('change', () => picker.form?.submit());
  });

  function formatMoney(cents, currency = theme.currency || 'CHF') {
    const locale = theme.locale === 'de' ? 'de-CH' : theme.locale === 'fr' ? 'fr-CH' : 'en-CH';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format((Number(cents) || 0) / 100);
  }

  function publicProductTitle(item) {
    if (isPartnerGift(item)) return theme.strings?.partnerBenefitTitle || item.product_title;
    const handle = String(item?.handle || '').toLowerCase();
    if (handle.includes('water-resistant')) return theme.strings?.waterResistantTitle || item.product_title;
    if (handle.includes('mesh')) return theme.strings?.meshTitle || item.product_title;
    return item?.product_title || '';
  }

  function renderCart(cart) {
    const empty = select('[data-cart-empty]');
    const items = select('[data-cart-items]');
    const summary = select('[data-cart-summary]');
    const template = select('[data-cart-item-template]');
    const customerCount = customerItemCount(cart);
    const isEmpty = customerCount === 0;
    const partner = activePartner(cart);
    let cartDiscountTitle = theme.strings?.discount || '';
    let founderBenefitPresent = false;

    selectAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = String(customerCount);
    });
    selectAll('[data-cart-drawer-count]').forEach((counter) => {
      counter.textContent = String(customerCount);
    });

    empty?.classList.toggle('hidden', !isEmpty);
    items?.classList.toggle('hidden', isEmpty);
    summary?.classList.toggle('hidden', isEmpty);

    if (items && template) {
      items.replaceChildren();
      cart.items.forEach((item) => {
        const fragment = template.content.cloneNode(true);
        const article = select('.cart-item', fragment);
        const imageLink = select('.cart-image', fragment);
        const image = select('img', fragment);
        const title = select('.cart-item-title', fragment);
        const variant = select('[data-cart-variant]', fragment);
        const linePrice = select('[data-cart-line-price]', fragment);
        const discounts = select('[data-cart-line-discounts]', fragment);
        const linePayable = select('[data-cart-line-payable]', fragment);
        const quantity = select('[data-cart-item-quantity]', fragment);
        const decrease = select('[data-cart-decrease]', fragment);
        const increase = select('[data-cart-increase]', fragment);
        const remove = select('[data-cart-remove]', fragment);
        const quantityControl = select('.quantity-control', fragment);
        const partnerGift = isPartnerGift(item);

        article.dataset.cartKey = item.key;
        imageLink.href = item.url;
        title.href = item.url;
        title.textContent = publicProductTitle(item);
        image.src = item.image || '';
        image.hidden = !item.image;
        variant.textContent = item.variant_title === 'Default Title' ? '' : item.variant_title;
        variant.hidden = !variant.textContent;
        linePrice.textContent = formatMoney(
          item.original_line_price > item.final_line_price ? item.original_line_price : item.final_line_price,
          cart.currency,
        );
        quantity.textContent = String(item.quantity);
        decrease.dataset.cartQuantity = String(Math.max(0, item.quantity - 1));
        increase.dataset.cartQuantity = String(item.quantity + 1);
        remove.dataset.cartQuantity = '0';

        if (partnerGift) {
          article.classList.add('cart-item-partner-gift');
          article.dataset.partnerBenefitItem = '';
          imageLink.hidden = true;
          imageLink.removeAttribute('href');
          title.removeAttribute('href');
          title.setAttribute('aria-disabled', 'true');
          variant.textContent = '';
          variant.hidden = true;
          linePrice.textContent = theme.strings?.partnerBenefitIncluded
            ? partnerText(theme.strings.partnerBenefitIncluded, partner)
            : formatMoney(0, cart.currency);
          quantityControl.hidden = true;
          remove.hidden = true;
          if (linePayable) linePayable.hidden = true;
        }

        const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
        allocations.forEach((allocation) => {
          const note = document.createElement('p');
          note.className = 'cart-discount';
          const titleText = allocation.discount_application?.title || theme.strings?.discount || '';
          if (cartDiscountTitle === (theme.strings?.discount || '')) cartDiscountTitle = titleText;
          if (titleText.toLowerCase().includes('founder')) founderBenefitPresent = true;
          note.textContent = `${titleText}: -${formatMoney(allocation.amount, cart.currency)}`;
          discounts.append(note);
        });
        if (linePayable) {
          const discounted = item.original_line_price > item.final_line_price;
          linePayable.hidden = !discounted;
          const payableAmount = select('strong', linePayable);
          if (payableAmount) payableAmount.textContent = formatMoney(item.final_line_price, cart.currency);
        }

        items.append(fragment);
      });
    }

    const subtotal = select('[data-cart-subtotal]');
    const total = select('[data-cart-total]');
    const discount = select('[data-cart-discount]');
    const discountLabel = select('[data-cart-discount-label]');
    const discountRow = select('[data-cart-discount-row]');
    const benefitConfirmation = select('[data-cart-benefit-confirmation]');
    if (subtotal) {
      const originalSubtotal = cart.original_total_price ?? (cart.items_subtotal_price + cart.total_discount);
      subtotal.textContent = formatMoney(originalSubtotal, cart.currency);
    }
    if (total) total.textContent = formatMoney(cart.total_price, cart.currency);
    if (discount) discount.textContent = `-${formatMoney(cart.total_discount, cart.currency)}`;
    if (discountLabel) discountLabel.textContent = cartDiscountTitle;
    discountRow?.classList.toggle('hidden', !cart.total_discount);
    benefitConfirmation?.classList.toggle('hidden', !founderBenefitPresent);
  }

  async function requestCart(path, options = {}) {
    const response = await fetch(ajaxUrl(path), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.headers || {}) },
      ...options,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || theme.strings?.cartError);
    return payload;
  }

  async function synchronizePartnerBenefit(cart) {
    let currentCart = cart;
    let changed = false;
    const partner = activePartner(currentCart);

    if (partner) {
      const attributes = currentCart.attributes || {};
      if (attributes['Partner source'] !== partner.code || attributes['Partner benefit'] !== 'Extra cushioning insole pair') {
        currentCart = await requestCart('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              ...attributes,
              'Partner source': partner.code,
              'Partner benefit': 'Extra cushioning insole pair',
            },
          }),
        });
        changed = true;
      }
    }

    const desiredQuantity = partner ? eligibleShoeQuantity(currentCart) : 0;
    let giftItems = currentCart.items.filter(isPartnerGift);

    if (desiredQuantity === 0) {
      for (const giftItem of giftItems) {
        currentCart = await requestCart('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
        });
        changed = true;
      }
      return { cart: currentCart, partner, changed };
    }

    if (!giftItems.length) {
      await requestCart('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: giftVariantId, quantity: desiredQuantity }] }),
      });
      currentCart = await requestCart('cart.js');
      changed = true;
      giftItems = currentCart.items.filter(isPartnerGift);
    }

    const primaryGift = giftItems[0];
    if (primaryGift && primaryGift.quantity !== desiredQuantity) {
      currentCart = await requestCart('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: primaryGift.key, quantity: desiredQuantity }),
      });
      changed = true;
    }

    const duplicateGifts = currentCart.items.filter(isPartnerGift).slice(1);
    for (const giftItem of duplicateGifts) {
      currentCart = await requestCart('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
      });
      changed = true;
    }

    return { cart: currentCart, partner, changed };
  }

  async function refreshCart({ open = false, trigger } = {}) {
    const initialCart = await requestCart('cart.js');
    const synchronized = await synchronizePartnerBenefit(initialCart);
    renderPartnerBenefitNotice(synchronized.partner);
    renderCart(synchronized.cart);
    if (open) openDialog(select('[data-cart-dialog]'), trigger);
    return synchronized.cart;
  }

  selectAll('[data-cart-open]').forEach((trigger) => {
    trigger.addEventListener('click', async (event) => {
      event.preventDefault();
      try {
        await refreshCart({ open: true, trigger });
      } catch {
        window.location.assign(trigger.href);
      }
    });
  });

  select('[data-cart-items]')?.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-quantity], [data-cart-remove]');
    if (!control) return;
    const item = control.closest('[data-cart-key]');
    if (!item) return;
    const quantity = Number(control.dataset.cartQuantity ?? 0);
    control.disabled = true;
    try {
      const cart = await requestCart('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.dataset.cartKey, quantity }),
      });
      const synchronized = await synchronizePartnerBenefit(cart);
      renderCart(synchronized.cart);
    } catch (error) {
      select('[data-cart-live-region]').textContent = error.message;
    } finally {
      control.disabled = false;
    }
  });

  selectAll('[data-product-form], .footle-product-form').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      const errorBox = select('[data-product-form-error]', form);
      const submit = select('[type="submit"]', form);
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }
      submit?.setAttribute('aria-busy', 'true');
      if (submit) submit.disabled = true;
      try {
        await requestCart('cart/add.js', { method: 'POST', body: new FormData(form) });
        select('[data-cart-live-region]').textContent = theme.strings?.addedToCart || '';
        await refreshCart({ open: true, trigger: submit });
      } catch (error) {
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = error.message;
        }
      } finally {
        submit?.removeAttribute('aria-busy');
        if (submit) {
          const variantInput = select('[data-product-variant-id]', form);
          submit.disabled = variantInput ? variantInput.disabled : false;
        }
      }
    });
  });

  function handleize(value = '') {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function priceNumber(cents) {
    return formatMoney(cents);
  }

  function syncGalleryUi(gallery, visible, index) {
    const active = visible[index];
    const current = select('[data-gallery-current]', gallery);
    const total = select('[data-gallery-total]', gallery);
    const view = select('[data-gallery-view]', gallery);
    if (current) current.textContent = visible.length ? String(index + 1) : '0';
    if (total) total.textContent = String(visible.length);
    if (view) view.textContent = active?.dataset.mediaView || '';
    selectAll('[data-gallery-thumbnail]', gallery).forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.galleryThumbnail === active?.dataset.mediaId));
    });
  }

  function renderGalleryThumbnails(gallery, visible) {
    const container = select('[data-gallery-thumbnails]', gallery);
    if (!container) return;
    container.replaceChildren();
    visible.forEach((item, index) => {
      const source = select('img', item);
      if (!source) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.galleryThumbnail = item.dataset.mediaId;
      button.setAttribute('aria-label', `${item.dataset.mediaView || source.alt} · ${index + 1} / ${visible.length}`);
      button.setAttribute('aria-pressed', String(index === 0));
      const image = document.createElement('img');
      image.src = source.currentSrc || source.src;
      image.alt = '';
      image.loading = 'lazy';
      button.append(image);
      button.addEventListener('click', () => {
        gallery._footleActiveIndex = index;
        item.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
        syncGalleryUi(gallery, visible, index);
        select('[data-gallery-previous]', gallery).disabled = index === 0;
        select('[data-gallery-next]', gallery).disabled = index === visible.length - 1;
      });
      container.append(button);
    });
  }

  function setGalleryColor(gallery, color, featuredMediaId = 0) {
    if (!gallery) return;
    const colorHandle = handleize(color);
    const items = selectAll('[data-gallery-item]', gallery);
    items.forEach((item) => {
      const mediaColor = item.dataset.mediaColor || '';
      item.hidden = Boolean(mediaColor && colorHandle && mediaColor !== colorHandle);
    });

    const visible = items.filter((item) => !item.hidden);
    const target = visible.find((item) => Number(item.dataset.mediaId) === Number(featuredMediaId)) || visible[0];
    target?.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'auto' });

    const previous = select('[data-gallery-previous]', gallery);
    const next = select('[data-gallery-next]', gallery);
    if (previous) previous.disabled = true;
    if (next) next.disabled = visible.length <= 1;

    gallery._footleVisibleItems = visible;
    gallery._footleActiveIndex = 0;
    renderGalleryThumbnails(gallery, visible);
    syncGalleryUi(gallery, visible, 0);
  }

  function moveGallery(gallery, direction) {
    const visible = gallery?._footleVisibleItems || selectAll('[data-gallery-item]:not([hidden])', gallery);
    if (!visible.length) return;
    const currentIndex = Number(gallery._footleActiveIndex || 0);
    const nextIndex = Math.min(visible.length - 1, Math.max(0, currentIndex + direction));
    gallery._footleActiveIndex = nextIndex;
    visible[nextIndex].scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
    syncGalleryUi(gallery, visible, nextIndex);
    select('[data-gallery-previous]', gallery).disabled = nextIndex === 0;
    select('[data-gallery-next]', gallery).disabled = nextIndex === visible.length - 1;
  }

  function updateLedger(wrapper, variant) {
    const ledger = select('[data-price-ledger]', wrapper.closest('[data-product-root]') || document);
    if (!ledger || !variant) return;
    const metafieldBenefit = Number(ledger.dataset.benefitMetafieldCents || 0);
    const compareAt = Number(variant.compareAtPrice || 0);
    const price = Number(variant.price || 0);
    let mode = 'price-only';
    let standard = price;
    let benefit = 0;
    let today = price;

    if (compareAt > price) {
      mode = 'compare-at';
      standard = compareAt;
      benefit = compareAt - price;
    } else if (metafieldBenefit > 0 && metafieldBenefit < price) {
      mode = 'metafield';
      benefit = metafieldBenefit;
      today = price - metafieldBenefit;
    }

    ledger.dataset.ledgerMode = mode;
    ledger.dataset.currentVariantId = String(variant.id);
    const standardRow = select('[data-ledger-standard-row]', ledger);
    const benefitRow = select('[data-ledger-benefit-row]', ledger);
    standardRow?.classList.toggle('hidden', mode === 'price-only');
    benefitRow?.classList.toggle('hidden', mode === 'price-only');
    const standardPrice = select('[data-ledger-standard-price]', ledger);
    const benefitAmount = select('[data-ledger-benefit-amount]', ledger);
    const totalPrice = select('[data-ledger-total-price]', ledger);
    if (standardPrice) standardPrice.textContent = formatMoney(standard);
    if (benefitAmount) benefitAmount.textContent = `− ${formatMoney(benefit)}`;
    if (totalPrice) totalPrice.textContent = formatMoney(today);
    const cartNote = select('[data-benefit-cart-note]', wrapper.closest('[data-product-root]') || document);
    const missingNote = select('[data-benefit-missing-note]', wrapper.closest('[data-product-root]') || document);
    cartNote?.classList.toggle('hidden', mode !== 'metafield');
    missingNote?.classList.toggle('hidden', mode !== 'price-only');
  }

  function initializeProduct(wrapper) {
    const data = select('[data-product-variants]', wrapper);
    if (!data) return;
    let variants = [];
    try {
      variants = JSON.parse(data.textContent);
    } catch {
      return;
    }

    const form = select('.footle-product-form', wrapper);
    const variantInput = select('[data-product-variant-id]', wrapper);
    const submit = select('[data-product-submit]', wrapper);
    const optionGroups = selectAll('[data-product-option]', wrapper);
    const gallery = select('[data-product-gallery]', wrapper.closest('[data-product-root]') || document);
    const marketAllowed = wrapper.dataset.marketAllowed === 'true';
    const hasExplicitVariant = Boolean(wrapper.dataset.initialVariantId);
    const requestedColor = hasExplicitVariant ? '' : handleize(new URL(window.location.href).searchParams.get('color') || '');
    const values = new Map();

    optionGroups.forEach((group) => {
      const position = Number(group.dataset.optionPosition) - 1;
      const colorButtons = selectAll('[data-option-value]', group);
      const requestedButton = requestedColor
        ? colorButtons.find((button) => !button.disabled && handleize(button.dataset.optionValue) === requestedColor)
        : null;
      if (requestedButton) {
        colorButtons.forEach((button) => {
          const selected = button === requestedButton;
          button.setAttribute('aria-pressed', String(selected));
          button.classList.toggle('selected', selected);
        });
        const requestedLabel = select('[data-selected-option-label]', group);
        if (requestedLabel) requestedLabel.textContent = requestedButton.dataset.optionValue;
      }
      const selectedButton = requestedButton || select('[data-option-value][aria-pressed="true"]', group);
      const selectControl = select('[data-option-select]', group);
      values.set(position, selectedButton?.dataset.optionValue || selectControl?.value || '');

      selectAll('[data-option-value]', group).forEach((button) => {
        button.classList.toggle('selected', button.getAttribute('aria-pressed') === 'true');
        button.addEventListener('click', () => {
          selectAll('[data-option-value]', group).forEach((peer) => {
            const selected = peer === button;
            peer.setAttribute('aria-pressed', String(selected));
            peer.classList.toggle('selected', selected);
          });
          values.set(position, button.dataset.optionValue);
          const label = select('[data-selected-option-label]', group);
          if (label) label.textContent = button.dataset.optionValue;
          optionGroups.forEach((otherGroup) => {
            const otherSelect = select('[data-option-select]', otherGroup);
            if (otherSelect && Number(otherGroup.dataset.optionPosition) - 1 !== position) {
              otherSelect.value = '';
              otherSelect.classList.remove('has-size');
              values.set(Number(otherGroup.dataset.optionPosition) - 1, '');
              selectAll('[data-size-value]', otherGroup).forEach((sizeButton) => sizeButton.setAttribute('aria-pressed', 'false'));
            }
          });
          updateProductState();
        });
      });

      selectControl?.addEventListener('change', () => {
        values.set(position, selectControl.value);
        selectControl.classList.toggle('has-size', Boolean(selectControl.value));
        selectAll('[data-size-value]', group).forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.sizeValue === selectControl.value)));
        updateProductState();
      });

      selectAll('[data-size-value]', group).forEach((button) => {
        button.addEventListener('click', () => {
          if (!selectControl) return;
          selectControl.value = button.dataset.sizeValue;
          selectControl.classList.add('has-size');
          values.set(position, button.dataset.sizeValue);
          selectAll('[data-size-value]', group).forEach((peer) => peer.setAttribute('aria-pressed', String(peer === button)));
          updateProductState();
        });
      });
    });

    function variantMatches(candidate, position, value) {
      return candidate.options[position] === value;
    }

    function updateAvailability() {
      optionGroups.forEach((group) => {
        const position = Number(group.dataset.optionPosition) - 1;
        const selectControl = select('[data-option-select]', group);
        if (!selectControl) return;
        selectAll('option[value]', selectControl).forEach((option) => {
          if (!option.value) return;
          const canSelect = variants.some((variant) => {
            if (!variant.available || !marketAllowed || !variantMatches(variant, position, option.value)) return false;
            return Array.from(values.entries()).every(([otherPosition, selectedValue]) => {
              if (otherPosition === position || !selectedValue) return true;
              return variantMatches(variant, otherPosition, selectedValue);
            });
          });
          option.disabled = !canSelect;
          const sizeButton = select(`[data-size-value="${CSS.escape(option.value)}"]`, group);
          if (sizeButton) sizeButton.disabled = !canSelect;
        });
      });
    }

    let galleryObserver;
    function observeGalleryItems() {
      if (!gallery || !('IntersectionObserver' in window)) return;
      galleryObserver?.disconnect();
      const track = select('[data-gallery-track]', gallery);
      const visible = gallery._footleVisibleItems || [];
      galleryObserver = new IntersectionObserver((entries) => {
        const active = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!active) return;
        const index = visible.indexOf(active.target);
        if (index < 0) return;
        gallery._footleActiveIndex = index;
        const previous = select('[data-gallery-previous]', gallery);
        const next = select('[data-gallery-next]', gallery);
        syncGalleryUi(gallery, visible, index);
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === visible.length - 1;
      }, { root: track, threshold: [0.6, 0.85] });
      visible.forEach((item) => galleryObserver.observe(item));
    }

    function updateProductState() {
      updateAvailability();
      const selectedOptions = Array.from({ length: optionGroups.length }, (_, position) => values.get(position) || '');
      const complete = selectedOptions.every(Boolean);
      const variant = complete
        ? variants.find((candidate) => candidate.options.every((value, position) => value === selectedOptions[position]))
        : null;

      if (variantInput) {
        variantInput.value = variant?.id || '';
        variantInput.disabled = !variant || !variant.available || !marketAllowed;
      }

      if (submit) {
        const active = Boolean(variant?.available && marketAllowed);
        const ledger = select('[data-price-ledger]', wrapper.closest('[data-product-root]') || document);
        const metafieldBenefit = Number(ledger?.dataset.benefitMetafieldCents || 0);
        const effectivePrice = variant
          ? (variant.compareAtPrice > variant.price
            ? variant.price
            : (metafieldBenefit > 0 && metafieldBenefit < variant.price ? variant.price - metafieldBenefit : variant.price))
          : 0;
        submit.disabled = !active;
        submit.classList.toggle('button-accent', active);
        submit.classList.toggle('button-disabled', !active);
        if (!marketAllowed) submit.textContent = theme.strings?.switzerlandMarketOnly || '';
        else if (variant && !variant.available) submit.textContent = theme.strings?.soldOut || '';
        else if (!variant) submit.textContent = theme.strings?.selectSizeFirst || '';
        else {
          const template = submit.dataset.addLabelTemplate || theme.strings?.addToCart || '';
          submit.textContent = template.replace(/\{\{\s*price\s*\}\}/g, priceNumber(effectivePrice));
        }
      }

      const colorGroup = optionGroups.find((group) => select('[data-option-value]', group));
      const colorPosition = colorGroup ? Number(colorGroup.dataset.optionPosition) - 1 : -1;
      const color = colorPosition >= 0 ? values.get(colorPosition) : '';
      setGalleryColor(gallery, color, variant?.featuredMediaId || 0);
      observeGalleryItems();
      if (variant) {
        updateLedger(wrapper, variant);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url);
      } else {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }

      const mobileBar = select('[data-mobile-buy-bar]', wrapper.closest('[data-product-root]') || document);
      const mobileSize = select('[data-mobile-size-button]', mobileBar || document);
      const mobileAdd = select('[data-mobile-add]', mobileBar || document);
      if (mobileSize) mobileSize.textContent = variant?.title || theme.strings?.selectSize || '';
      if (mobileAdd) {
        const mobileActive = Boolean(variant?.available && marketAllowed);
        mobileAdd.disabled = !mobileActive;
        mobileAdd.classList.toggle('active', mobileActive);
        const ledger = select('[data-price-ledger]', wrapper.closest('[data-product-root]') || document);
        const metafieldBenefit = Number(ledger?.dataset.benefitMetafieldCents || 0);
        const effectivePrice = variant
          ? (variant.compareAtPrice > variant.price
            ? variant.price
            : (metafieldBenefit > 0 && metafieldBenefit < variant.price ? variant.price - metafieldBenefit : variant.price))
          : 0;
        const template = mobileAdd.dataset.addShortTemplate || mobileBar?.dataset.addShortTemplate || '';
        mobileAdd.textContent = variant?.available && marketAllowed
          ? template.replace(/\{\{\s*price\s*\}\}/g, priceNumber(effectivePrice))
          : (!marketAllowed ? theme.strings?.switzerlandMarketOnly : theme.strings?.selectSizeFirst);
      }
    }

    select('[data-gallery-previous]', gallery)?.addEventListener('click', () => moveGallery(gallery, -1));
    select('[data-gallery-next]', gallery)?.addEventListener('click', () => moveGallery(gallery, 1));

    const primarySubmit = submit;
    const footer = select('[data-site-footer]');
    const mobileBar = select('[data-mobile-buy-bar]', wrapper.closest('[data-product-root]') || document);
    if (mobileBar && primarySubmit && 'IntersectionObserver' in window) {
      let primaryVisible = false;
      let footerVisible = false;
      const updateBar = () => mobileBar.classList.toggle('visible', !primaryVisible && !footerVisible);
      new IntersectionObserver(([entry]) => {
        primaryVisible = entry.isIntersecting;
        updateBar();
      }, { threshold: 0.2 }).observe(primarySubmit);
      if (footer) {
        new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          updateBar();
        }, { threshold: 0.01 }).observe(footer);
      }
    }

    select('[data-mobile-size-button]', mobileBar || document)?.addEventListener('click', () => {
      const sizeGrid = select('[data-size-options]', wrapper);
      const selectedSize = select('[data-size-value][aria-pressed="true"]:not(:disabled)', sizeGrid || wrapper);
      const firstAvailableSize = select('[data-size-value]:not(:disabled)', sizeGrid || wrapper);
      sizeGrid?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      (selectedSize || firstAvailableSize)?.focus({ preventScroll: true });
    });
    select('[data-mobile-add]', mobileBar || document)?.addEventListener('click', () => {
      if (submit?.disabled) {
        select('[data-mobile-size-button]', mobileBar)?.click();
      } else {
        form?.requestSubmit(submit);
      }
    });

    updateProductState();
  }

  selectAll('[data-product-form-wrapper]').forEach(initializeProduct);

  async function initializePartnerBenefit() {
    if (giftProductHandle && window.location.pathname.includes(`/products/${giftProductHandle}`)) {
      window.location.replace(ajaxUrl('collections/frontpage?view=footle'));
      return;
    }
    renderPartnerBenefitNotice(activePartner(null));
    const cart = await requestCart('cart.js');
    const synchronized = await synchronizePartnerBenefit(cart);
    renderPartnerBenefitNotice(synchronized.partner);
    renderCart(synchronized.cart);
    if (synchronized.changed && document.body.classList.contains('template-cart')) {
      window.location.reload();
    }
  }

  initializePartnerBenefit().catch(() => {});

  window.FootleCart = { refresh: refreshCart, render: renderCart, formatMoney };
})();
